import { useState, useMemo, useRef, useCallback } from "react";
import {
  parseISO,
  subMonths,
  startOfYear,
  startOfDay,
  endOfDay,
  isValid,
} from "date-fns";
import { Locale } from "date-fns";
import { DateFilterType, DateFilterOption } from "../types";
import { formatStatementDate } from "../utils";

interface UseStatementDateFilterParams {
  tr: (key: string) => string;
  dateLocale?: Locale;
}

export function useStatementDateFilter({
  tr,
  dateLocale,
}: UseStatementDateFilterParams) {
  const [dateFilter, setDateFilter] = useState<DateFilterType>("all");
  const [customStartDate, setCustomStartDate] = useState<string>("");
  const [customEndDate, setCustomEndDate] = useState<string>("");
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState<boolean>(false);
  const dateDropdownRef = useRef<HTMLDivElement>(null);

  const dateFilterOptions: DateFilterOption[] = useMemo(
    () => [
      {
        value: "all",
        label: tr("All time"),
        description: tr("Show invoices from all time"),
      },
      {
        value: "last_12_months",
        label: tr("Last 12 months"),
        description: tr("Invoices from the past 12 months"),
      },
      {
        value: "current_year",
        label: tr("Current year (Since January 1st)"),
        description: tr("Invoices created since January 1st of current year"),
      },
      {
        value: "custom",
        label: tr("Custom range"),
        description: tr("Filter by custom start and end date"),
      },
    ],
    [tr]
  );

  const activeDateLabel = useMemo(() => {
    if (dateFilter === "all") return tr("All time");
    if (dateFilter === "last_12_months") return tr("Last 12 months");
    if (dateFilter === "current_year") return tr("Current year (Since January 1st)");
    if (dateFilter === "custom") {
      if (customStartDate && customEndDate) {
        return `${formatStatementDate(customStartDate, dateLocale)} – ${formatStatementDate(
          customEndDate,
          dateLocale
        )}`;
      }
      if (customStartDate) {
        return `${tr("From")} ${formatStatementDate(customStartDate, dateLocale)}`;
      }
      if (customEndDate) {
        return `${tr("Until")} ${formatStatementDate(customEndDate, dateLocale)}`;
      }
      return tr("Custom range");
    }
    return tr("All time");
  }, [dateFilter, customStartDate, customEndDate, tr, dateLocale]);

  const handleClearCustomDates = useCallback(() => {
    setCustomStartDate("");
    setCustomEndDate("");
  }, []);

  const matchesDateFilter = useCallback(
    (creationDateStr?: string | null) => {
      if (dateFilter === "all" || !creationDateStr) return true;

      const invDate = parseISO(creationDateStr);
      if (!isValid(invDate)) return true;

      const now = new Date();
      if (dateFilter === "last_12_months") {
        const twelveMonthsAgo = startOfDay(subMonths(now, 12));
        const endOfToday = endOfDay(now);
        return invDate >= twelveMonthsAgo && invDate <= endOfToday;
      }

      if (dateFilter === "current_year") {
        const startOfCurrentYear = startOfYear(now);
        return invDate >= startOfCurrentYear;
      }

      if (dateFilter === "custom") {
        if (customStartDate) {
          const start = startOfDay(parseISO(customStartDate));
          if (isValid(start) && invDate < start) {
            return false;
          }
        }
        if (customEndDate) {
          const end = endOfDay(parseISO(customEndDate));
          if (isValid(end) && invDate > end) {
            return false;
          }
        }
        return true;
      }

      return true;
    },
    [dateFilter, customStartDate, customEndDate]
  );

  return {
    dateFilter,
    setDateFilter,
    customStartDate,
    setCustomStartDate,
    customEndDate,
    setCustomEndDate,
    isDateDropdownOpen,
    setIsDateDropdownOpen,
    dateDropdownRef,
    dateFilterOptions,
    activeDateLabel,
    handleClearCustomDates,
    matchesDateFilter,
  };
}
